import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    TextField,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Tooltip
} from '@mui/material';
import axios from 'axios';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const ROLES = ['manager', 'lead', 'editor', 'qa', 'proofreader', 'translator'];

const TeamPermissionsMatrix = ({ token }) => {
    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                // We'll fetch the team data which now includes settings.permissions
                const res = await axios.get('/api/team/member-teams', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Find the team where current user is owner
                const ownedTeam = res.data.find(t => t.status === 'owner');
                if (ownedTeam && ownedTeam.settings?.permissions) {
                    setPermissions(ownedTeam.settings.permissions);
                } else {
                    setError("Could not find owned team permissions.");
                }
            } catch (err) {
                console.error("Error fetching permissions:", err);
                setError("Failed to load team settings.");
            } finally {
                setLoading(false);
            }
        };
        fetchPermissions();
    }, [token]);

    const handleCheckboxChange = (role, field) => {
        setPermissions(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                [field]: !prev[role][field]
            }
        }));
    };

    const handleQuotaChange = (role, value) => {
        setPermissions(prev => ({
            ...prev,
            [role]: {
                ...prev[role],
                mtQuota: parseInt(value) || 0
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            await axios.put('/api/team/settings/permissions', 
                { permissions }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error("Error saving permissions:", err);
            setError("Failed to save changes.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress />;
    if (!permissions) return <Alert severity="warning">No team permissions found to configure.</Alert>;

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h5" gutterBottom>
                Team Permissions Matrix
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure exactly what each team role can access and use. Settings apply team-wide.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>Permissions saved successfully!</Alert>}

            <TableContainer component={Paper} elevation={2}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell><strong>Role</strong></TableCell>
                            <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <strong>Use MT</strong>
                                    <Tooltip title="Allow this role to use Machine Translation (DeepL/Google)">
                                        <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.6 }} />
                                    </Tooltip>
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <strong>MT Quota</strong>
                                    <Tooltip title="Monthly character limit per member in this role">
                                        <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.6 }} />
                                    </Tooltip>
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <strong>Edit TM</strong>
                                    <Tooltip title="Allow members to save new translations to the Team Memory">
                                        <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.6 }} />
                                    </Tooltip>
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <strong>Edit Glossary</strong>
                                    <Tooltip title="Allow members to add/edit/delete terms in the team glossaries">
                                        <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.6 }} />
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ROLES.map((role) => {
                            const config = permissions[role] || permissions.translator;
                            return (
                                <TableRow key={role}>
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                        <strong>{role}</strong>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox 
                                            checked={config.canUseMT}
                                            onChange={() => handleCheckboxChange(role, 'canUseMT')}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <TextField 
                                            size="small"
                                            type="number"
                                            value={config.mtQuota}
                                            onChange={(e) => handleQuotaChange(role, e.target.value)}
                                            sx={{ width: 120 }}
                                            InputProps={{ inputProps: { min: 0 } }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox 
                                            checked={config.canEditTM}
                                            onChange={() => handleCheckboxChange(role, 'canEditTM')}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox 
                                            checked={config.canEditGlossary}
                                            onChange={() => handleCheckboxChange(role, 'canEditGlossary')}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ px: 4, py: 1, borderRadius: 2 }}
                >
                    {saving ? 'Saving...' : 'Save Permissions'}
                </Button>
            </Box>
        </Box>
    );
};

export default TeamPermissionsMatrix;
